'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';

export interface ActionsDropdownItem {
  label: string;
  icon?: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

export interface ActionsDropdownProps {
  items: ActionsDropdownItem[];
  /** Custom trigger button (optional, defaults to three-dot menu) */
  trigger?: React.ReactNode;
  /** Minimum width of the dropdown menu */
  minWidth?: number;
  /** Alignment offset from trigger button right edge */
  alignOffset?: number;
  /** Disable the entire dropdown */
  disabled?: boolean;
  /** Custom style for the container */
  style?: React.CSSProperties;
}

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  position: relative;
`;

const MenuButton = styled.div<{ $disabled?: boolean }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  position: relative;
  z-index: 10;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  
  &:hover {
    background-color: ${({ $disabled }) => ($disabled ? 'transparent' : '#f3f4f6')};
  }
  
  &:focus {
    outline: ${({ $disabled }) => ($disabled ? 'none' : '2px solid #FB6F2A')};
    outline-offset: 2px;
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: #6b7280;
  }
`;

const MenuDropdown = styled.div<{ $minWidth?: number }>`
  position: fixed;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  min-width: ${({ $minWidth }) => $minWidth || 180}px;
  z-index: 99;
  overflow: visible;
  backdrop-filter: blur(8px);
  max-height: 400px;
  overflow-y: auto;
`;

const MenuItem = styled.button<{ $variant?: 'default' | 'danger'; $disabled?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  text-align: left;
  font-size: 14px;
  color: ${({ $variant, $disabled }) => {
    if ($disabled) return '#9ca3af';
    return $variant === 'danger' ? '#dc2626' : '#374151';
  }};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 12px;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  
  &:hover {
    background-color: ${({ $variant, $disabled }) => {
      if ($disabled) return 'transparent';
      return $variant === 'danger' ? '#fef2f2' : '#f5f5f5';
    }};
  }
  
  &:focus {
    outline: none;
    background-color: #f3f4f6;
  }
  
  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

const DefaultTrigger = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

/**
 * ActionsDropdown Component
 * 
 * A reusable dropdown menu component with smart positioning that adjusts
 * based on viewport space. Automatically positions above or below the trigger
 * button to prevent overflow.
 * 
 * @component
 * @example
 * ```tsx
 * <ActionsDropdown
 *   items={[
 *     {
 *       label: 'Edit',
 *       icon: <EditIcon />,
 *       onClick: () => handleEdit(),
 *     },
 *     {
 *       label: 'Delete',
 *       icon: <DeleteIcon />,
 *       variant: 'danger',
 *       onClick: () => handleDelete(),
 *     },
 *   ]}
 * />
 * ```
 */
export const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  items,
  trigger,
  minWidth = 180,
  alignOffset = 180,
  disabled = false,
  style,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Update menu position based on button position
  const updateMenuPosition = useCallback(() => {
    if (buttonRef.current && menuRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuHeight = menuRef.current.offsetHeight || 200;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Check if dropdown would overflow bottom of viewport
      const shouldShowAbove = spaceBelow < menuHeight + 10 && spaceAbove > menuHeight;
      
      setMenuPosition({
        top: shouldShowAbove ? rect.top - menuHeight - 4 : rect.bottom + 4,
        left: rect.right - alignOffset,
      });
    }
  }, [alignOffset]);

  // Find scrollable parent elements
  const findScrollableParents = useCallback((element: HTMLElement | null): HTMLElement[] => {
    const scrollableParents: HTMLElement[] = [];
    let current: HTMLElement | null = element;
    
    while (current && current !== document.body) {
      const overflow = window.getComputedStyle(current).overflow;
      if (overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') {
        scrollableParents.push(current);
      }
      current = current.parentElement;
    }
    
    return scrollableParents;
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen && buttonRef.current) {
      document.addEventListener('mousedown', handleClickOutside);
      
      // Update position on window scroll (capture phase to catch all scroll events)
      window.addEventListener('scroll', updateMenuPosition, true);
      
      // Update position on window resize
      window.addEventListener('resize', updateMenuPosition);
      
      // Find and listen to scrollable parent containers
      const scrollableParents = findScrollableParents(buttonRef.current);
      scrollableParents.forEach(parent => {
        parent.addEventListener('scroll', updateMenuPosition, true);
      });
      
      // Initial position update - with a slight delay to ensure menu is rendered
      updateMenuPosition();
      const timeoutId = setTimeout(updateMenuPosition, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
        scrollableParents.forEach(parent => {
          parent.removeEventListener('scroll', updateMenuPosition, true);
        });
      };
    }
  }, [menuOpen, updateMenuPosition, findScrollableParents]);

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (disabled) return;
    
    if (!menuOpen && buttonRef.current) {
      updateMenuPosition();
    }
    
    setMenuOpen(!menuOpen);
  };

  const handleItemClick = (item: ActionsDropdownItem, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (item.disabled) {
      return;
    }
    
    item.onClick(e);
    setMenuOpen(false);
  };

  return (
    <ActionsContainer >
      <MenuButton
        ref={buttonRef}
        onClick={handleMenuToggle}
        $disabled={disabled}
        style={style}
      >
        {trigger || <DefaultTrigger />}
      </MenuButton>
      {menuOpen && (
        <MenuDropdown
          ref={menuRef}
          $minWidth={minWidth}
          style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
        >
          {items.map((item, index) => (
            <MenuItem
              key={index}
              $variant={item.variant}
              $disabled={item.disabled}
              onClick={(e) => handleItemClick(item, e)}
            >
              {item.icon}
              {item.label}
            </MenuItem>
          ))}
        </MenuDropdown>
      )}
    </ActionsContainer>
  );
};

